/*
 * VerificationPanel: the suites an appliance can run on itself to check the
 * product's claims, and their verdicts.
 *
 * Lists the suites from /verify, each by its title, a one-line summary and its
 * last verdict. The selected one leads with its result: the run's own plain
 * sentence and its measurements. How the test works, its settings, the rule
 * it grades against, what it cannot see and where the run was made are one
 * click away rather than on screen, because a page of fine print is a page no
 * one reads; all of it travels with Copy result. A run is confirmed before it
 * starts, polled while it runs, and can be cancelled.
 *
 * Every word here is the appliance's own: a suite's claim, rule and limits
 * come from the edge, so the panel cannot promise more than the suite grades.
 *
 * A result says whether the appliance signed it, offers the signed file, and
 * says how to check it elsewhere. The console never checks a signature itself:
 * the box calling its own signature valid would be the box grading itself, the
 * on-device console is not a secure context, and browsers have no ML-DSA.
 *
 * ! file components/VerificationPanel.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2026
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiPlay, FiSquare, FiRefreshCw, FiCopy } from "react-icons/fi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { fmtDuration } from "../utils/formatters";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { useConfirm } from "./ConfirmModal";
import { useWanName } from "./WanNames";
import { useSession } from "./PortalGate";
import FeatureUnavailableNotice from "./FeatureUnavailableNotice";
import DownloadButton from "./DownloadButton";
import { IS_SINGLE_EDGE } from "../serverApi";
import RESULTS, { KeyValueResult, VerdictBadge, verdictClass, muted, resolveWords, coverageOf, reportText, RULE_SOURCE }
  from "./VerificationResults";

const POLL_RUNNING = 3000;
const POLL_IDLE = 20000;
/* A suite estimated at over half an hour is polled every 30 s while it runs and
   every minute otherwise: its progress moves by the minute, and the selected
   suite's poll carries its whole report each time. */
const LONG_RUN_SEC = 30 * 60;
const POLL_RUNNING_LONG = 30000;
const POLL_IDLE_LONG = 60000;

/* A run changes a live appliance, so notabox-server requires the operator role
   for it. A viewer reads results, which is most of what this panel is for. */
const READ_ONLY = "Starting a run needs the operator role. Your account can read results.";

// A run's length: minutes, then hours to a tenth, then days for a soak.
const fmtMins = (sec) => {
  const m = Math.max(1, Math.round(sec / 60));
  if (sec >= 86400) return `about ${fmtDuration(sec)}`;
  if (m >= 90) return `about ${Math.round(m / 6) / 10} hours`;
  return `about ${m} minute${m === 1 ? "" : "s"}`;
};
const fmtTime = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
};
const fmtEstimate = (sec) => {
  const m = Math.max(1, Math.round((sec || 0) / 60));
  if ((sec || 0) >= 86400) return `~${fmtDuration(sec)}`;
  return m >= 90 ? `~${Math.round(m / 6) / 10} h` : `~${m} min`;
};
// A card has room for "Sep 13, 10:34 PM", not a full timestamp.
const fmtWhen = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? ""
    : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const IMPACT = {
  low: { label: "Low impact", cls: "is-low" },
  moderate: { label: "Moderate impact", cls: "is-moderate" },
  high: { label: "High impact", cls: "is-high" },
};

/* How much a run disturbs the edge, in one word; what that means is the
   suite's own disruption text, on hover and in How it works. */
/* The impact of the run a form describes. A parameter set away from its zero
   value can make a run more disruptive than the suite's default, and says so
   in its spec; the worst of those is what the operator is warned about. */
const IMPACT_RANK = { low: 0, moderate: 1, high: 2 };
export function runImpact(desc, valueOf) {
  let impact = desc?.impact || "low";
  for (const spec of desc?.params || []) {
    const v = valueOf(spec);
    const set = Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && v !== "" && v !== 0 && v !== "0" && v !== false;
    if (spec.impact && set && (IMPACT_RANK[spec.impact] || 0) > (IMPACT_RANK[impact] || 0)) impact = spec.impact;
  }
  return impact;
}

function ImpactChip({ impact, title }) {
  const i = IMPACT[impact];
  return i ? <span className={`vfy-chip ${i.cls}`} title={title}>{i.label}</span> : null;
}

/* Detail a reader asks for rather than wades through. A native disclosure, so it
   works from the keyboard and to a screen reader with nothing added. */
function More({ label, count, children }) {
  return (
    <details className="vfy-more">
      <summary>
        {label}
        {count > 0 && <span className="vfy-more-count">{count}</span>}
      </summary>
      <div className="vfy-more-body">{children}</div>
    </details>
  );
}

const btn = (fill) => ({
  display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8,
  border: `1px solid ${fill}`, background: fill, color: "#fff", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer",
});
const input = { fontSize: "0.78rem", padding: "5px 8px", borderRadius: 8, border: "1px solid #cbd5e1", width: 90 };

/* The value a parameter carries in the run body, typed as the suite declared it. */
function coerce(spec, v) {
  switch (spec.type) {
    case "int": return parseInt(v, 10);
    case "float": return parseFloat(v);
    case "bool": return !!v;
    case "list": return Array.isArray(v) ? v : [];
    default: return String(v);
  }
}

/* One control per parameter; the form holds only what the operator changed.
   An option reads as the suite names it, else by its circuit's display name:
   the value posted is always the option itself. */
function ParamField({ spec, value, onChange, disabled, wanName }) {
  const id = `verify-param-${spec.name}`;
  const optionLabel = (o) => spec.option_labels?.[o] || (o ? wanName(o) : "(none)");
  const unit = spec.unit ? <span style={muted}> {spec.unit}</span> : null;
  let control;
  switch (spec.type) {
    case "bool":
      control = <input id={id} type="checkbox" checked={!!value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />;
      break;
    case "string":
      // A number box would refuse an address or a URL outright.
      control = (
        <input id={id} type="text" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
          spellCheck={false} style={{ ...input, width: 200 }} />
      );
      break;
    case "enum":
      control = (
        <select id={id} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} style={{ ...input, width: "auto" }}>
          {(spec.options || []).map((o) => <option key={o} value={o}>{optionLabel(o)}</option>)}
        </select>
      );
      break;
    case "list":
      control = (
        <span role="group" aria-labelledby={`${id}-label`} style={{ display: "inline-flex", gap: 10 }}>
          {(spec.options || []).map((o) => (
            <label key={o} style={{ fontSize: "0.78rem", display: "inline-flex", alignItems: "center", gap: 4 }}>
              <input type="checkbox" checked={(value || []).includes(o)} disabled={disabled}
                onChange={(e) => onChange(e.target.checked ? [...(value || []), o] : (value || []).filter((x) => x !== o))} />
              {optionLabel(o)}
            </label>
          ))}
        </span>
      );
      break;
    default:
      control = (
        <input id={id} type="number" value={value} disabled={disabled} min={spec.min} max={spec.max}
          step={spec.type === "float" ? "any" : 1} onChange={(e) => onChange(e.target.value)} style={input} />
      );
  }
  // A parameter that makes the run more disruptive than the suite's default
  // says so beside its box once it is set, in the words the chip uses.
  const raised = spec.impact && IMPACT[spec.impact] && runImpact({ params: [spec] }, () => value) === spec.impact;
  return (
    <label htmlFor={id} id={`${id}-label`} title={spec.help} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.78rem", color: "#334155" }}>
      <span>{spec.label}</span>
      {control}{unit}
      {raised && <span className={`vfy-chip ${IMPACT[spec.impact].cls}`}>{IMPACT[spec.impact].label} with this set</span>}
    </label>
  );
}

const DISCLOSES = "The file carries this run's provenance and settings, including any far end they name.";

// A person compares a key by its first 16 characters, which is what a pin asks for.
const shortKey = (digest) => String(digest || "").slice(0, 16);
const sentence = (s) => (/[.!?]$/.test(s) ? s : `${s}.`);

/* How the appliance recorded the result on screen. Nothing at all for a build
   that does not seal, which is not the same statement as "not signed". What a
   signature does not show is the appliance's sentence, stated at the weight of
   the line it qualifies, never as fine print under it. */
function SealLine({ seal, shows }) {
  if (!seal) return null;
  if (!seal.signed) return <div className="vfy-seal is-unsigned">Not signed: {sentence(seal.unsigned || "no reason given")}</div>;
  return (
    <>
      <div className="vfy-seal">Signed by this appliance, run {seal.seq} in sequence, key {shortKey(seal.signer)}.</div>
      {shows && <div className="vfy-seal">{shows}</div>}
    </>
  );
}

/* What the next run will be signed with. Amber when notabox-server publishes a
   different key for this appliance, because a buyer pins the key it publishes. */
function SigningNote({ signing }) {
  if (!signing) return null;
  if (!signing.enabled) {
    return <div className="vfy-signing">Results will not be signed: {sentence(signing.unsigned || "no reason given")}</div>;
  }
  const differs = signing.published === "differs";
  return (
    <div className={`vfy-signing${differs ? " is-warn" : ""}`}>
      Results will be signed with key {shortKey(signing.signer)}.
      {differs && " The key notabox-server publishes for this appliance is a different one, so a key pinned through it will not match."}
    </div>
  );
}

/* Where a pin command read through the portal leaves the digest to the reader. */
const EXPECT_PLACEHOLDER = "<first 16 characters of the digest on the appliance console>";

/* Everything a reader needs to check the downloaded file without trusting this
   page: the whole digest, the two commands, and what other tools cannot see.

   Only the appliance's own console fills in the digest a pin expects. Through
   the portal this page is relayed by notabox-server, and a pin must never rest
   on what it relays: the digest is shown for comparison, and the command keeps
   a place for the one read off the appliance. */
function CheckItYourself({ seal, node, file, tools }) {
  const pinFile = `${node}.pin.json`;
  const expect = IS_SINGLE_EDGE ? shortKey(seal.signer) : EXPECT_PLACEHOLDER;
  return (
    <>
      <div className="vfy-words-title">
        {IS_SINGLE_EDGE ? "This appliance's key digest" : "This appliance's key digest, as notabox-server relays it"}
      </div>
      <code className="vfy-digest">{seal.signer}</code>
      <div className="vfy-words-body">
        {IS_SINGLE_EDGE
          ? "Pin the key once, against this digest as the appliance's own console shows it."
          : "For comparison only. Pin the key once, against the digest the appliance's own console or log shows, "
            + "not this page: this page reaches the appliance through notabox-server."}
      </div>
      <pre className="vfy-cmd">
        {`nabx-verify-report pin -server https://<notabox-server> -node ${node} -expect ${expect} -o ${pinFile}\n`
          + `nabx-verify-report check -key ${pinFile} ${file}`}
      </pre>
      {tools && <div className="vfy-words-body">{tools}</div>}
    </>
  );
}

const sealLabel = (seal) => (seal.record === "missing" ? "no record"
  : `run ${seal.seq}, ${seal.signed ? "signed" : "unsigned"}`);

function ProvenanceLine({ p, ended, wanName }) {
  if (!p) return null;
  const parts = [
    p.node_id && `node ${p.node_id}`,
    p.build?.revision && `build ${p.build.revision.slice(0, 10)}${p.build.modified ? " (modified)" : ""}`,
    p.anchor_host && `anchor ${p.anchor_host}`,
    p.wans?.length > 0 && `WANs ${p.wans.map((w) => wanName(w.name)).join(" + ")}`,
    `impairments ${p.impairments_active?.length ? p.impairments_active.map(wanName).join(", ") : "none"}`,
    p.classifier_mode && `classifier ${p.classifier_mode}`,
    ended && `ended ${ended}`,
  ].filter(Boolean);
  return <div className="vfy-prov">{parts.join(" · ")}</div>;
}

export default function VerificationPanel({ nodeApiUrl, selectedNode }) {
  const [suites, setSuites] = useState(null);
  const [signing, setSigning] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [selected, setSelected] = useState("");
  // The suite being looked at, readable by the poll without making the poll
  // depend on it: a picked suite is fetched at the moment it is picked, not at
  // the next idle poll twenty seconds later, which would show a suite whose
  // card says PASS as never run.
  const selectedRef = useRef("");
  const choose = useCallback((id) => {
    selectedRef.current = id;
    setSelected(id);
  }, []);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const [fallbackText, setFallbackText] = useState("");
  const confirm = useConfirm();
  const wanName = useWanName();
  const { canWrite } = useSession();
  // Every reply is stamped with the edge it was asked of, so a slow answer for
  // the previous edge cannot land on this one.
  const asked = useRef(0);

  const url = useCallback((p) => (nodeApiUrl ? nodeApiUrl(p) : null), [nodeApiUrl]);

  // null means the appliance predates the feature; anything else is a body.
  const get = useCallback(async (path) => {
    const r = await fetchWithTimeout(url(path), {}, 10000);
    if (r.status === 404 || r.status === 501) return null;
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  }, [url]);

  const load = useCallback(async () => {
    if (!url("/verify")) return;
    const seq = ++asked.current;
    const stale = () => seq !== asked.current;
    try {
      const list = await get("/verify");
      if (stale()) return;
      if (list === null) {
        setUnavailable(true);
        setSuites(null);
        setStatus(null);
        return;
      }
      setUnavailable(false);
      const descs = list.suites || [];
      setSuites(descs);
      setSigning(list.signing || null);
      // Membership, not truthiness. A selection carried from another edge, or
      // one this edge has stopped offering, would otherwise keep naming a suite
      // that is not in this list, and the status below it would stay whatever it
      // last was: the previous appliance's verdict under this one's name.
      const want = selectedRef.current;
      const id = descs.some((d) => d.id === want) ? want : descs[0]?.id;
      if (id !== want) choose(id || "");
      if (!id) {
        setStatus(null);
        setError("");
        return;
      }
      const st = await get(`/verify/${id}`);
      if (stale()) return;
      setStatus(st || null);
      setError("");
    } catch (e) {
      if (stale()) return;
      setError(e.message || "Failed to load verification state");
    }
  }, [choose, get, url]);

  // Nothing on screen belongs to the newly selected edge until it answers, and
  // invalidating the sequence means the old edge's reply in flight is discarded
  // rather than rendered under the new edge's name.
  useEffect(() => {
    asked.current++;
    setSuites(null);
    setSigning(null);
    choose("");
    setStatus(null);
    setUnavailable(false);
    setError("");
    setBusy(false);
    setCopied("");
    setFallbackText("");
  }, [choose, selectedNode]);

  const current = status && status.descriptor?.id === selected ? status : null;
  const running = !!current?.running;
  // The list entry is this edge's current answer; status.descriptor may be a
  // reply that is one poll old.
  const desc = (suites || []).find((s) => s.id === selected) || current?.descriptor;
  const long = (desc?.estimate_sec || 0) > LONG_RUN_SEC;
  useVisibleInterval(load, running ? (long ? POLL_RUNNING_LONG : POLL_RUNNING) : (long ? POLL_IDLE_LONG : POLL_IDLE));
  useEffect(() => { setForm({}); setCopied(""); setFallbackText(""); }, [selected]);

  const post = useCallback(async (path, body) => {
    const r = await fetchWithTimeout(url(path), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body || {}),
    }, 15000);
    if (r.ok) return;
    // The edge answers with http.Error, so the body is a plain sentence. It is
    // the right thing to show for a rejected parameter or an unavailable suite;
    // a refusal the operator can act on gets said in the console's terms.
    const body_ = (await r.text()).trim();
    if (r.status === 403) throw new Error(READ_ONLY);
    if (r.status === 409 && path.endsWith("/run")) {
      throw new Error("A verification run is already in progress on this edge. Only one runs at a time, "
        + "and it may be a different suite or started from another session.");
    }
    throw new Error(body_ || `HTTP ${r.status}`);
  }, [url]);

  const params = desc?.params || [];
  const valueOf = (spec) => (form[spec.name] !== undefined ? form[spec.name] : spec.default);
  // What the run the form describes does to the appliance: the suite's own
  // impact, raised by any parameter set away from its zero value that says so.
  const impact = runImpact(desc, valueOf);

  const start = useCallback(async () => {
    const ok = await confirm({
      title: `Run "${desc.title}" on ${selectedNode || "this edge"}?`,
      // Only a run that disturbs the appliance is asked to wait for a quiet period;
      // a low impact one is meant for a live site.
      message: `${desc.disruption}\n\nEstimated ${fmtMins(desc.estimate_sec || 0)} at the default settings. ` +
        `${impact === "low" ? "" : "Run it in a quiet period. "}It can be cancelled at any time.`,
      confirmLabel: "Start run",
      variant: "danger",
    });
    if (!ok) return;
    const body = {};
    for (const spec of desc?.params || []) body[spec.name] = coerce(spec, form[spec.name] !== undefined ? form[spec.name] : spec.default);
    setBusy(true);
    try {
      await post(`/verify/${desc.id}/run`, body);
      setError("");
      await load(); // the run is asynchronous; pick up "running" from the edge
    } catch (e) {
      setError(e.message || "Failed to start the run");
    } finally {
      setBusy(false);
    }
  }, [confirm, desc, form, impact, load, post, selectedNode]);

  const cancel = useCallback(async () => {
    setBusy(true);
    try {
      await post(`/verify/${desc.id}/cancel`);
      await load();
    } catch (e) {
      setError(e.message || "Failed to cancel");
    } finally {
      setBusy(false);
    }
  }, [desc, load, post]);

  if (unavailable) {
    return (
      <div className="panel">
        <FeatureUnavailableNotice nodeId={selectedNode} feature="Verification" />
      </div>
    );
  }

  const last = current?.last;
  const progress = current?.progress;
  const history = current?.history || [];
  // Absent on a build that does not seal, and then nothing about signing is said.
  const lastSeal = last ? history.find((h) => h.id === last.id)?.seal : null;
  const node = last?.provenance?.node_id || selectedNode || "node";
  // The name the appliance gives the download, so the check command matches it.
  const exportFile = last ? `${[last.provenance?.node_id, desc?.id, last.id].filter(Boolean).join("-")}.dsse.json` : "";
  const words = resolveWords(desc, last);
  const coverage = last ? coverageOf(last) : null;
  // The on-device console is served over plain HTTP from the appliance, which
  // is not a secure context, so navigator.clipboard is undefined there. Falling
  // back to a selectable box keeps the result forwardable on the one build a
  // pilot customer actually uses.
  // Deliberately not a useCallback: it closes over last, which is resolved below
  // the unavailable branch above, and a hook after an early return changes the
  // hook order when an appliance stops serving the route mid-session.
  const copy = async () => {
    const text = reportText({ desc, report: last, wanName });
    if (!navigator.clipboard?.writeText) {
      setFallbackText(text);
      setCopied("Select the text below and copy it.");
      return;
    }
    await navigator.clipboard.writeText(text);
    setCopied("Copied.");
  };

  const canRun = !!desc && desc.available && !running && !busy && canWrite;
  const startTitle = !canWrite ? READ_ONLY
    : desc?.available ? `Takes ${fmtMins(desc.estimate_sec || 0)} at the default settings`
      : desc?.reason;
  const Result = (desc && RESULTS[desc.id]) || KeyValueResult;
  const steps = desc?.steps || [];

  // The rule a result was graded against and what the suite cannot see. Before
  // any run they are the suite's current words; beside a result they are the
  // report's, labelled as such, and they are stated in one place only.
  const fineprint = (
    <>
      <div className="vfy-words-head">
        <div className="vfy-words-title">Pass rule</div>
        {last && <div className="vfy-words-src">{words.recorded ? RULE_SOURCE.recorded : RULE_SOURCE.live}</div>}
      </div>
      <div className="vfy-words-body">{(last ? words.rule : desc?.rule) || "not stated"}</div>
      {last && words.drifted && (
        <div className="vfy-drift">This appliance now grades by a different rule than the one above.</div>
      )}
      {(last ? words.limits : desc?.limits || []).length > 0 && (
        <>
          <div className="vfy-words-title" style={{ marginTop: 11 }}>What this cannot see</div>
          <ul className="vfy-limits">{(last ? words.limits : desc.limits).map((l) => <li key={l}>{l}</li>)}</ul>
        </>
      )}
    </>
  );

  return (
    <div>
      {error && <div className="panel-error" role="alert">{error}</div>}

      <div className="vfy-suites">
        {(suites || []).map((s) => {
          // The list carries every suite's last verdict; the selected one's
          // report, when it is newer, is the fresher answer.
          const seen = s.id === selected && last ? last : s.latest;
          return (
            <button key={s.id} type="button" onClick={() => { choose(s.id); load(); }} aria-pressed={s.id === selected}
              className={`vfy-suite${s.id === selected ? " is-selected" : ""}${s.available ? "" : " is-off"}`}>
              <div className="vfy-suite-title">{s.title}</div>
              <div className="vfy-suite-claim">{s.summary || s.claim}</div>
              <div className="vfy-suite-foot">
                {seen ? <VerdictBadge verdict={seen.verdict} /> : <span className="vfy-suite-when">Not run yet</span>}
                {seen && <span className="vfy-suite-when">{fmtWhen(seen.started_at)}</span>}
              </div>
              <div className="vfy-suite-meta">
                {[fmtEstimate(s.estimate_sec), IMPACT[s.impact]?.label].filter(Boolean).join(" · ")}
              </div>
              {!s.available && <div className="vfy-suite-why" title={s.reason}>Not available now: {s.reason}</div>}
            </button>
          );
        })}
        {suites && suites.length === 0 && <div style={muted}>This edge offers no verification suites.</div>}
      </div>

      {desc && (
        <div className="panel">
          <div className="vfy-head">
            <div className="vfy-head-text">
              <div className="panel-title">{desc.title}</div>
              <div className="vfy-summary">{desc.summary || desc.claim}</div>
            </div>
            <div className="vfy-head-actions">
              <span className="vfy-chip">{fmtEstimate(desc.estimate_sec)}</span>
              <ImpactChip impact={impact} title={desc.disruption} />
              {running ? (
                <button onClick={cancel} disabled={busy || !canWrite} style={btn("#475569")}
                  title={canWrite ? "Stop the run; what was measured is kept" : READ_ONLY}>
                  <FiSquare size={12} /> Stop
                </button>
              ) : (
                <button onClick={start} disabled={!canRun} style={{ ...btn("#4f46e5"), opacity: canRun ? 1 : 0.5 }}
                  title={startTitle}>
                  <FiPlay size={12} /> Start
                </button>
              )}
            </div>
          </div>

          {!canWrite && <div className="vfy-suite-why" style={{ marginTop: 0 }}>{READ_ONLY}</div>}
          {!desc.available && <div className="vfy-suite-why">Not available now: {desc.reason}</div>}
          <SigningNote signing={signing} />

          <div className="vfy-mores">
            <More label="How it works" count={steps.length}>
              {steps.length > 0
                ? <ol className="vfy-steps">{steps.map((st) => <li key={st}>{st}</li>)}</ol>
                : <div className="vfy-words-body">{desc.claim}</div>}
              <div className="vfy-while"><b>While it runs:</b> {desc.disruption}</div>
            </More>
            {params.length > 0 && (
              <More label="Settings" count={params.length}>
                <div className="vfy-params">
                  {params.map((spec) => (
                    <ParamField key={spec.name} spec={spec} value={valueOf(spec)} disabled={running || busy}
                      wanName={wanName} onChange={(v) => setForm((f) => ({ ...f, [spec.name]: v }))} />
                  ))}
                </div>
              </More>
            )}
            {!last && <More label="Pass rule and limits">{fineprint}</More>}
          </div>

          {running && progress && (
            <div className="vfy-run">
              <div className="vfy-run-head">
                <FiRefreshCw size={13} className="aspath-spin" aria-hidden="true" />
                Running · block {progress.block} of {progress.blocks} · {progress.condition}
                <span className="vfy-run-note">({progress.note})</span>
              </div>
              <div className="vfy-run-track" role="progressbar" aria-label="Verification run progress"
                aria-valuemin={0} aria-valuemax={progress.total || 0} aria-valuenow={progress.done || 0}>
                <div className="vfy-run-fill"
                  style={{ width: `${progress.total ? (100 * progress.done) / progress.total : 0}%` }} />
              </div>
              <div className="vfy-run-foot">
                {progress.done} of {progress.total} steps · {fmtMins(progress.eta_sec || 0)} left
              </div>
            </div>
          )}

          {/* What the run in flight has found so far, drawn the way its result
              will be. It has no verdict and is not signed, and says so. */}
          {running && current.live && (
            <div className="vfy-result" aria-label="Found so far">
              <div className="vfy-words-head">
                <div className="vfy-words-title">So far</div>
                <div className="vfy-words-src">live, not final and not signed</div>
              </div>
              <Result report={current.live} wanName={wanName} />
            </div>
          )}
          {running && last && <div className="vfy-samples" style={{ marginTop: 14 }}>The last finished run:</div>}

          {last ? (
            <div className={`vfy-result ${verdictClass(last.verdict)}`}>
              {/* The run writes its own one-sentence result. It leads because it
                  is what a non-technical reader takes away, and it is the
                  appliance's words rather than the console's. */}
              {last.plain && <div className="vfy-plain">{last.plain}</div>}
              <div className="vfy-verdict">
                <VerdictBadge verdict={last.verdict} label={last.finding} />
                <span style={{ fontSize: "0.8rem", color: "#0f172a", fontWeight: 600 }}>{fmtTime(last.started_at)}</span>
              </div>
              <SealLine seal={lastSeal} shows={signing?.shows} />
              {/* What the run says to do about its own result: only it knows why
                  it fell short, so only it can name the setting that changes it. */}
              {last.next && (
                <div className="vfy-next" role="note" style={{
                  margin: "10px 0 2px", padding: "8px 10px", borderRadius: 8, fontSize: "0.82rem", lineHeight: 1.45,
                  background: "#fffbeb", border: "1px solid #fcd34d", color: "#78350f",
                }}>
                  <b>What to do.</b> {last.next}
                </div>
              )}

              {/* A run kept without its measurements has none to draw, and its
                  report's zeros are not readings. */}
              {!last.measurements_lost && <Result report={last} wanName={wanName} />}

              {coverage && (
                <div className="vfy-samples" style={coverage.complete ? undefined : { color: "#92400e" }}>
                  {coverage.text}
                </div>
              )}

              <div className="vfy-mores">
                <More label="Details">
                  <div className="vfy-words-title">Why</div>
                  <div className="vfy-reason" style={{ marginTop: 4 }}>{last.reason}</div>
                  <div className="vfy-words">{fineprint}</div>
                  <div className="vfy-ident">run {last.id} on node {last.provenance?.node_id || "unknown"}</div>
                  <ProvenanceLine p={last.provenance} ended={last.ended} wanName={wanName} />
                </More>
                {lastSeal?.signed && (
                  <More label="Check it yourself">
                    <CheckItYourself seal={lastSeal} node={node} file={exportFile} tools={signing?.tools} />
                  </More>
                )}
              </div>

              <div className="vfy-actions">
                <button type="button" onClick={copy} style={{ ...btn("#475569"), background: "#fff", color: "#475569" }}
                  title="The verdict with its rule, limits, measurements and provenance, as text">
                  <FiCopy size={12} /> Copy result
                </button>
                {lastSeal && (
                  <>
                    <DownloadButton href={url(`/verify/${desc.id}/export/${last.id}`)} filename={exportFile}
                      label={lastSeal.signed ? "Download signed report" : "Download unsigned report"} />
                    <span style={muted}>{DISCLOSES}</span>
                  </>
                )}
                <span style={muted} aria-live="polite">{copied}</span>
              </div>
              {fallbackText && (
                <textarea readOnly value={fallbackText} onFocus={(e) => e.target.select()}
                  aria-label="Verification result, select and copy" className="vfy-paste" />
              )}
            </div>
          ) : (
            !running && <div className="vfy-samples">Not run on this edge yet.</div>
          )}

          {history.length > 1 && (
            <div className="vfy-mores">
              <More label="Previous runs" count={Math.min(history.length - 1, 5)}>
                <div className="vfy-samples" style={{ marginBottom: 4 }}>
                  Verdicts only. The rule each was graded against is not recorded with them.
                </div>
                {history.slice(1, 6).map((h) => (
                  <div className="vfy-hist-row" key={h.id}>
                    <span className="vfy-hist-when">{fmtTime(h.started_at)}</span>
                    <VerdictBadge verdict={h.verdict} />
                    <span>{h.headline}</span>
                    {h.seal && <span className="vfy-hist-seal">{sealLabel(h.seal)}</span>}
                  </div>
                ))}
              </More>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
